import DocsLayout, { Callout } from '../../components/docs/DocsLayout';

export default function DocsFees() {
  return (
    <DocsLayout 
      title="Fees" 
      description="Fee structure and revenue sharing"
    >
      <section className="space-y-6">
        <p className="text-white/70 leading-relaxed">
          AIBL charges fees on certain operations to sustain protocol development and reward token stakers. This page explains our fee structure and how revenue is distributed.
        </p>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Fee Schedule</h2>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-4 text-white/50 font-medium">Operation</th>
                <th className="text-left py-3 px-4 text-white/50 font-medium">Fee</th>
                <th className="text-left py-3 px-4 text-white/50 font-medium">Notes</th>
              </tr>
            </thead>
            <tbody className="text-white/70">
              <tr className="border-b border-white/5">
                <td className="py-3 px-4">Swap</td>
                <td className="py-3 px-4">0.1%</td>
                <td className="py-3 px-4">Of swap volume</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-3 px-4">Bridge</td>
                <td className="py-3 px-4">0.15%</td>
                <td className="py-3 px-4">Of bridged amount</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-3 px-4">Transfer</td>
                <td className="py-3 px-4">Free</td>
                <td className="py-3 px-4">Gas only</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-3 px-4">Wrap/Unwrap</td>
                <td className="py-3 px-4">Free</td>
                <td className="py-3 px-4">Gas only</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-3 px-4">Safe Deployment</td>
                <td className="py-3 px-4">0.01 ETH</td>
                <td className="py-3 px-4">One-time fee</td>
              </tr>
              <tr>
                <td className="py-3 px-4">Agent Creation</td>
                <td className="py-3 px-4">Free</td>
                <td className="py-3 px-4">First 2 agents free</td>
              </tr>
            </tbody>
          </table>
        </div>

        <Callout type="info" title="Gas Fees">
          All operations also incur blockchain gas fees which go directly to network validators. AIBL does not profit from gas fees.
        </Callout>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Staker Discounts</h2>
        
        <p className="text-white/70 mb-4">
          AIBL token stakers receive fee discounts based on their tier:
        </p>

        <div className="space-y-3">
          <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
            <span className="text-white/70">Bronze (1,000+ AIBL)</span>
            <span className="text-green-400 font-medium">5% discount</span>
          </div>
          <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
            <span className="text-white/70">Silver (10,000+ AIBL)</span>
            <span className="text-green-400 font-medium">15% discount</span>
          </div>
          <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
            <span className="text-white/70">Gold (50,000+ AIBL)</span>
            <span className="text-green-400 font-medium">25% discount</span>
          </div>
          <div className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20">
            <span className="text-yellow-400">Platinum (100,000+ AIBL)</span>
            <span className="text-yellow-400 font-medium">50% discount</span>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Revenue Distribution</h2>
        
        <p className="text-white/70 mb-4">
          Protocol fees are distributed as follows:
        </p>

        <div className="space-y-3">
          <div className="flex items-center gap-4">
            <div className="w-24 text-sm text-white/70">Stakers</div>
            <div className="flex-1 bg-white/5 rounded-full h-4 overflow-hidden">
              <div className="bg-green-500 h-full" style={{ width: '50%' }} />
            </div>
            <div className="w-12 text-sm text-white/70 text-right">50%</div>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-24 text-sm text-white/70">Treasury</div>
            <div className="flex-1 bg-white/5 rounded-full h-4 overflow-hidden">
              <div className="bg-blue-500 h-full" style={{ width: '30%' }} />
            </div>
            <div className="w-12 text-sm text-white/70 text-right">30%</div>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-24 text-sm text-white/70">Team</div>
            <div className="flex-1 bg-white/5 rounded-full h-4 overflow-hidden">
              <div className="bg-purple-500 h-full" style={{ width: '20%' }} />
            </div>
            <div className="w-12 text-sm text-white/70 text-right">20%</div>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Volume Tiers</h2>
        
        <p className="text-white/70 mb-4">
          High-volume users may qualify for additional discounts:
        </p>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-4 text-white/50 font-medium">Monthly Volume</th>
                <th className="text-left py-3 px-4 text-white/50 font-medium">Additional Discount</th>
              </tr>
            </thead>
            <tbody className="text-white/70">
              <tr className="border-b border-white/5">
                <td className="py-3 px-4">$100K - $500K</td>
                <td className="py-3 px-4">5%</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-3 px-4">$500K - $1M</td>
                <td className="py-3 px-4">10%</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-3 px-4">$1M - $5M</td>
                <td className="py-3 px-4">15%</td>
              </tr>
              <tr>
                <td className="py-3 px-4">$5M+</td>
                <td className="py-3 px-4">Custom</td>
              </tr>
            </tbody>
          </table>
        </div>

        <Callout type="success" title="Fee Caps">
          Staking and volume discounts stack, but total discount is capped at 70% to ensure protocol sustainability.
        </Callout>
      </section>
    </DocsLayout>
  );
}
